/**
 * Authentication API Service
 * Handles login, logout, and token management
 */

import { post, get, setAuthToken, clearAuthToken } from './client';

interface LoginRequest {
  email: string;
  password: string;
  deviceName?: string;
  rememberMe?: boolean;
}

interface User {
  id: string;
  fullName: string;
  email: string;
  role: 'admin' | 'staff' | 'partner';
}

interface LoginResponse {
  user: User;
  token: {
    type: string;
    name: string | null;
    token: string;
    abilities: string[];
    lastUsedAt: string | null;
    expiresAt: string;
  };
  rememberMeToken: string | null;
}

interface UserResponse {
  id: string;
  fullName: string;
  email: string;
  role: string;
}

/**
 * Login with email and password
 */
export async function login(email: string, password: string, rememberMe = false): Promise<LoginResponse> {
  const response = await post<LoginResponse>('/login', {
    email,
    password,
    deviceName: 'Web Browser',
    rememberMe,
  });

  // Store the access token
  setAuthToken(response.token.token);

  // Store remember me token if provided
  if (response.rememberMeToken) {
    localStorage.setItem('remember_me_token', response.rememberMeToken);
  }

  // Store user data
  localStorage.setItem('auth_user', JSON.stringify(response.user));

  return response;
}

/**
 * Auto login with remember me token
 */
export async function autoLogin(): Promise<LoginResponse | null> {
  const rememberMeToken = localStorage.getItem('remember_me_token');

  if (!rememberMeToken) {
    return null;
  }

  try {
    const response = await post<LoginResponse>('/auto-login', {
      rememberMeToken,
    });

    // Update stored tokens
    setAuthToken(response.token.token);

    if (response.rememberMeToken) {
      localStorage.setItem('remember_me_token', response.rememberMeToken);
    }

    localStorage.setItem('auth_user', JSON.stringify(response.user));

    return response;
  } catch (error) {
    // Clear invalid remember me token
    localStorage.removeItem('remember_me_token');
    return null;
  }
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  try {
    await post('/logout');
  } catch (error) {
    // Ignore logout errors, still clear local data
    console.error('Logout API error:', error);
  } finally {
    clearAuthToken();
    localStorage.removeItem('remember_me_token');
    localStorage.removeItem('auth_user');
  }
}

/**
 * Get current authenticated user
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await get<UserResponse>('/user');
    return {
      id: response.id,
      fullName: response.fullName,
      email: response.email,
      role: response.role as User['role'],
    };
  } catch (error) {
    return null;
  }
}

/**
 * Get stored user data (without API call)
 */
export function getStoredUser(): User | null {
  const userData = localStorage.getItem('auth_user');
  if (!userData) return null;

  try {
    return JSON.parse(userData);
  } catch {
    return null;
  }
}

/**
 * Check if user has specific role
 */
export function hasRole(requiredRole: User['role']): boolean {
  const user = getStoredUser();
  if (!user) return false;

  // Admin has access to everything
  if (user.role === 'admin') return true;

  return user.role === requiredRole;
}
